<template>
  <div
    :class="{
      [prefixCls]: true,
      [`${prefixCls}-vertical`]: direction === 'vertical',
    }"
  >
    <slot></slot>
    <div
      v-if="aiAssistant"
      :class="`${prefixCls}-ai-assistant`"
      @click="toggleAiAssistantIFrame()"
    >
      <IconAiRobotColor :size="38" />
      <div :class="`${prefixCls}-ai-assistant-text`">
        <span>智能</span>
        <span>助手</span>
      </div>
    </div>
    <transition name="fade-in">
      <div
        v-if="backTop && backTopVisible"
        :class="`${prefixCls}-back-top`"
        @click="scrollToTop"
      >
        <slot name="back-top">
          <button :class="`${prefixCls}-back-top-btn`">
            <icon-to-top />
          </button>
        </slot>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  computed,
  watch,
  type PropType,
} from 'vue';
// @ts-ignore
import BTween from 'b-tween';
import { on, off, querySelector } from '../_utils/dom';
import { throttleByRaf } from '../_utils/throttle-by-raf';
import IconToTop from '../icon/icon-to-top';
import IconAiRobotColor from '../icon/icon-ai-robot-color';
import { getPrefixCls } from '../_utils/global-config';
import { isString } from '../_utils/is';
import {
  loadingTextContent,
  aiAssistantPageOrigin,
  loadingImage,
  defaultBackTopProps,
} from './config';
import type { Direction } from '../_utils/constant';

export default defineComponent({
  name: 'FloatButton',
  components: {
    IconToTop,
    IconAiRobotColor,
  },
  props: {
    /**
     * @zh 悬浮按钮的方向，是水平还是竖直
     * @en The direction of the float buttons. Can be horizontal or vertical
     * @values 'horizontal','vertical'
     */
    direction: {
      type: String as PropType<Direction>,
      default: 'vertical',
    },
    /**
     * @zh 容器
     * @en container
     */
    targetContainer: {
      type: [String, Object] as PropType<string | HTMLElement>,
    },
    /**
     * @zh 是否使用 BackTop，BackTop 组件 props, 不包含 targetContainer
     * @en Whether to use BackTop, Boolean or BackTop component props without "targetContainer"
     */
    backTop: {
      type: [Boolean, Object] as PropType<boolean | Record<string, any>>,
    },
    /**
     * @zh 是否使用 AI Assistant
     * @en Whether to use AI Assistant, Boolean
     */
    aiAssistant: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    /**
     * @zh AI Assistant origin
     * @en AI Assistant origin
     */
    aiAssistantOrigin: {
      type: String as PropType<string>,
      default: '',
    },
    /**
     * @zh 配置 AI Assistant 的 token
     * @en setting token for visiting AI Assistant
     */
    token: {
      type: String as PropType<string>,
    },
    /**
     * @zh 配置 AI Assistant 链接上的扩展参数
     * @en setting extend parameters for AI Assistant url
     */
    aiAssistantExtendParams: {
      type: Object as PropType<Record<string, any>>,
      default: () => ({}),
    },
  },
  emits: ['aiAssistantAuthFail', 'aiAssistantPermissionFail'],
  setup(props, { emit, expose }) {
    const prefixCls = getPrefixCls('float-button');
    const aiAssistantPage = `${prefixCls}-ai-assistant-page`;
    const aiAssistantPageLoading = `${prefixCls}-ai-assistant-page-loading`;
    const aiAssistantLoadingRef = ref<HTMLElement>();
    const backTopVisible = ref(false);
    const target = ref<HTMLElement>();
    const win = window.parent || window;
    const isWindow = !props.targetContainer;
    let timer: number;

    const backTopProps = computed(() => {
      return typeof props.backTop === 'object'
        ? {
            visibleHeight: props.backTop.visibleHeight ?? 200,
            easing: props.backTop.easing ?? 'quartOut',
            duration: props.backTop.duration ?? 200,
          }
        : defaultBackTopProps;
    });

    const scrollHandler = throttleByRaf(() => {
      if (target.value && props.backTop) {
        const { visibleHeight } = backTopProps.value;
        const { scrollTop } = target.value;
        backTopVisible.value = scrollTop >= visibleHeight;
      }
    });

    const getContainer = (container: string | HTMLElement) => {
      if (isString(container)) {
        return querySelector(container) as HTMLElement;
      }
      return container;
    };

    const getAiAssistant = (container: HTMLElement) => {
      return querySelector(`.${aiAssistantPage}`, container);
    };

    const getAiAssistantLoading = (container: HTMLElement) => {
      return querySelector(`.${aiAssistantPageLoading}`, container);
    };

    const aiAssistantURL = computed(() => {
      const params = new URLSearchParams({
        from: 'star-trek-arco-design-internal',
        ...(props.token
          ? {
              token: props.token,
              redirect: `/global-chat`,
              ...props.aiAssistantExtendParams,
            }
          : {}),
      }).toString();
      return `${
        props.aiAssistantOrigin
          ? props.aiAssistantOrigin
          : aiAssistantPageOrigin
      }?${params}`;
    });

    const openAssistant = () => {
      clearTimeout(timer);
      const container = isWindow
        ? document?.body
        : getContainer(props.targetContainer);
      let iframe = getAiAssistant(container);
      let loading = getAiAssistantLoading(container);
      if (iframe) {
        loading?.remove?.();
        iframe.remove?.();
        return;
      }
      loading = document.createElement('div');
      loading.className = aiAssistantPageLoading;
      const loadingImg = document.createElement('img');
      const loadingText = document.createElement('span');
      loadingImg.className = `${aiAssistantPageLoading}-img`;
      loadingText.className = `${aiAssistantPageLoading}-text`;
      loadingImg.src = loadingImage;
      loadingText.innerText = loadingTextContent;
      loading.append(loadingImg, loadingText);
      aiAssistantLoadingRef.value = loading;
      iframe = document.createElement('iframe');
      iframe.className = aiAssistantPage;
      iframe.setAttribute('frameborder', '0');
      (iframe as HTMLIFrameElement).src = aiAssistantURL.value;
      container.append(loading, iframe);
      /**
       * 20s 超时自动移除 loading
       */
      timer = window.setTimeout(() => {
        aiAssistantLoadingRef.value?.remove?.();
      }, 20 * 1000);
    };

    const closeAssistant = () => {
      clearTimeout(timer);
      const container = isWindow
        ? document?.body
        : getContainer(props.targetContainer);
      const iframe = getAiAssistant(container);
      const loading = getAiAssistantLoading(container);
      loading?.remove?.();
      iframe?.remove?.();
    };

    const toggleAiAssistantIFrame = (type: 'open' | 'close' = 'open') => {
      if (type === 'open') {
        openAssistant();
      } else {
        closeAssistant();
      }
    };

    const handleMessageFromAiAssistant = (e: MessageEvent) => {
      if (
        ['globalpand.cn', '127.0.0.1', 'localhost'].some((o) =>
          e.origin.includes(o)
        )
      ) {
        const { data } = e;
        if (data?.data?.ready && aiAssistantLoadingRef.value) {
          aiAssistantLoadingRef.value.remove?.();
        }
        if (data?.event === 'close') {
          toggleAiAssistantIFrame('close');
        }
        if (data?.event === 'authFail') {
          emit('aiAssistantAuthFail');
        }
        if (data?.event === 'permission') {
          emit('aiAssistantPermissionFail');
        }
      }
    };

    onMounted(() => {
      target.value = isWindow
        ? document?.documentElement
        : getContainer(props.targetContainer);
      if (target.value && props.backTop) {
        on(isWindow ? win : target.value, 'scroll', scrollHandler);
        scrollHandler();
      }
      if (props.aiAssistant) {
        on(win, 'message', handleMessageFromAiAssistant);
      }
    });

    onUnmounted(() => {
      if (!props.backTop) {
        return;
      }
      scrollHandler.cancel();
      if (target.value) {
        off(isWindow ? win : target.value, 'scroll', scrollHandler);
      }
      if (props.aiAssistant) {
        off(win, 'message', handleMessageFromAiAssistant);
      }
    });

    watch(
      () => props.aiAssistant,
      (value) => {
        off(win, 'message', handleMessageFromAiAssistant);
        if (value) {
          on(win, 'message', handleMessageFromAiAssistant);
        }
      }
    );

    const scrollToTop = () => {
      if (target.value) {
        const { scrollTop } = target.value;
        const { easing, duration } = backTopProps.value;
        const tween = new BTween({
          from: { scrollTop },
          to: { scrollTop: 0 },
          easing,
          duration,
          onUpdate: (keys: any) => {
            if (target.value) {
              target.value.scrollTop = keys.scrollTop;
            }
          },
        });
        tween.start();
      }
    };

    expose({
      openAssistant,
      closeAssistant,
      toggleAiAssistantIFrame,
    });

    return {
      prefixCls,
      backTopVisible,
      scrollToTop,
      toggleAiAssistantIFrame,
    };
  },
});
</script>
